import { isAppDebug } from '@/utils/appDebug';
import { createConsoleLogger } from '@/utils/devtools/console-logger';

export const PLAYER_SERVICES_URL_QUERY_PARAM = 'player_services_url';

const PLAYER_SERVICES_URL_STORAGE_KEY = 'player_services_url_override';
const CLEAR_VALUES = new Set(['', 'clear', 'false', 'off', '-1']);
// Production (`player-services.pluto.tv`) and branch (`v1-42-5.player-services.branch.pluto.tv`).
const ALLOWED_PLAYER_SERVICES_HOSTNAME =
    /^(?:v\d+-\d+-\d+\.)?player-services(?:\.branch)?\.(?:pluto\.tv|paramountplus\.com)$/;
const PLAYER_SERVICES_SCRIPT_PATH = '/smart-tag/smart.tag.js';
const { log: logPlayerServicesUrlOverride } = createConsoleLogger('playerServicesUrlOverride');

function isBrowser(): boolean {
    return typeof window !== 'undefined';
}

// The accepted value ends up as a `<script src>`, so anything broader than the Smart Tag
// script on a known player-services host is arbitrary code execution on a debug build.
function isValidPlayerServicesUrl(value: string): boolean {
    try {
        const url = new URL(value);
        return (
            url.protocol === 'https:' &&
            url.username === '' &&
            url.password === '' &&
            url.port === '' &&
            ALLOWED_PLAYER_SERVICES_HOSTNAME.test(url.hostname) &&
            url.pathname.endsWith(PLAYER_SERVICES_SCRIPT_PATH) &&
            // `loadSmartTag` appends `?onReady=` by concatenation, which a query or fragment breaks.
            url.search === '' &&
            url.hash === ''
        );
    } catch {
        return false;
    }
}

// Even touching `window.localStorage` throws a SecurityError when site data is blocked or the
// document is sandboxed. Unavailable storage means no persisted override, and the read runs
// during render, so none of these may throw or log.
function getStoredValue(): string | null {
    try {
        return window.localStorage.getItem(PLAYER_SERVICES_URL_STORAGE_KEY);
    } catch {
        return null;
    }
}

function setStoredValue(value: string): void {
    try {
        window.localStorage.setItem(PLAYER_SERVICES_URL_STORAGE_KEY, value);
    } catch {
        // Storage unavailable: the override still applies for this URL, it just won't persist.
    }
}

function removeStoredValue(): void {
    try {
        window.localStorage.removeItem(PLAYER_SERVICES_URL_STORAGE_KEY);
    } catch {
        // Storage unavailable: nothing could have been persisted to remove.
    }
}

/** Validated read with no cleanup or logging, so callers may run it during render. */
function readStoredPlayerServicesUrlOverride(): string | null {
    const stored = getStoredValue();

    return stored && isValidPlayerServicesUrl(stored) ? stored : null;
}

function warnRejectedPlayerServicesUrl(value: string, reason: string): void {
    logPlayerServicesUrlOverride('Ignoring invalid player_services_url override', {
        reason,
        value,
    });
}

/** `null` when the param is absent — an empty string is a deliberate clear value. */
function readPlayerServicesUrlQueryParam(): string | null {
    const params = new URLSearchParams(window.location.search);
    if (!params.has(PLAYER_SERVICES_URL_QUERY_PARAM)) return null;

    return params.get(PLAYER_SERVICES_URL_QUERY_PARAM)?.trim() ?? '';
}

/**
 * Reads `?player_services_url=` from the current URL and persists it in localStorage.
 * Pass `clear`, `false`, `off`, `-1`, or an empty value to remove the override manually.
 * No-ops when `APP_DEBUG` is disabled.
 */
export function syncPlayerServicesUrlOverrideFromQuery(): void {
    if (!isBrowser() || !isAppDebug()) return;

    const value = readPlayerServicesUrlQueryParam();
    if (value === null) return;

    if (CLEAR_VALUES.has(value.toLowerCase())) {
        removeStoredValue();
        return;
    }

    if (isValidPlayerServicesUrl(value)) {
        setStoredValue(value);
        return;
    }

    warnRejectedPlayerServicesUrl(value, 'not an allowed Smart Tag script URL');
}

/**
 * Resolves the active override without writing or logging, so it can run during render.
 * The query param wins over the persisted value; the sync above stores it after commit.
 * An invalid param falls through to the persisted override, matching how the sync leaves a
 * good stored value in place.
 */
export function resolvePlayerServicesUrlOverride(): string | null {
    if (!isBrowser() || !isAppDebug()) return null;

    const value = readPlayerServicesUrlQueryParam();

    if (value !== null) {
        if (CLEAR_VALUES.has(value.toLowerCase())) return null;
        if (isValidPlayerServicesUrl(value)) return value;
    }

    return readStoredPlayerServicesUrlOverride();
}

/** Returns a validated override from localStorage without reading the current URL. */
export function readPersistedPlayerServicesUrlOverride(): string | null {
    if (!isBrowser() || !isAppDebug()) return null;

    const stored = getStoredValue();
    if (!stored) return null;

    if (!isValidPlayerServicesUrl(stored)) {
        removeStoredValue();
        warnRejectedPlayerServicesUrl(
            stored,
            'persisted value is not an allowed Smart Tag script URL',
        );
        return null;
    }

    return stored;
}

/** Removes the persisted Smart Tag script URL override when `APP_DEBUG` is enabled. */
export function clearPlayerServicesUrlOverride(): void {
    if (!isBrowser() || !isAppDebug()) return;

    removeStoredValue();
}
